import { useTitle } from "@/hooks/use-title.hook.ts";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { TasksSectionContent } from "@/pages/tasks/components/TasksSectionContent.tsx";
import { TasksTodoCountText } from "@/pages/tasks/components/TasksTodoCountText.tsx";
import { useOpenTaskFromUrl, useTaskDueSplit } from "@/pages/tasks/hooks/tasks.hooks.ts";
import {
  useMyTodoTasks,
  useMyTasksSearch,
  useEmployeeTasksSearch,
} from "@/queries/employees/use-employee-tasks.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useEmployeeToday } from "@/queries/employees/use-employee-today.query.ts";
import { ApiTaskEmployeeRole, ApiTaskFilter, ApiTaskStatus } from "@/types/task.types.ts";
import { ApiTodayItem, ApiTodayItemType } from "@/types/employees/employee-today.types.ts";
import {
  Box,
  EmptyStateBase,
  Page,
  PaginationBlock,
  Stack,
  TabItem,
  Tabs,
} from "@skailer-com/uikit";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";

enum TasksIndexSection {
  todo = "todo",
  archived = "archived",
  completed = "completed",
  watching = "watching",
}

export function TasksIndex() {
  const { t } = useTranslation();
  const { section } = useParams();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const library = filtersLibrary();
  const { data: employeeMe } = useEmployeeMe();

  useTitle(t("tasks:my_tasks"));
  useOpenTaskFromUrl();

  useEffect(() => {
    analyticsService.trackEvent(analyticEvents.tasks.viewed);
  }, []);

  const currentSection = useMemo(() => {
    const valid = Object.values(TasksIndexSection).includes(section as TasksIndexSection);
    return valid ? (section as TasksIndexSection) : TasksIndexSection.todo;
  }, [section]);

  const currentSectionIndex = useMemo(() => {
    const indexMap: Record<TasksIndexSection, number> = {
      [TasksIndexSection.todo]: 0,
      [TasksIndexSection.archived]: 1,
      [TasksIndexSection.completed]: 2,
      [TasksIndexSection.watching]: 3,
    };
    return indexMap[currentSection] ?? 0;
  }, [currentSection]);

  const handleSectionChange = useCallback(
    (activeTab: number) => {
      const sectionMap: Record<number, TasksIndexSection> = {
        0: TasksIndexSection.todo,
        1: TasksIndexSection.archived,
        2: TasksIndexSection.completed,
        3: TasksIndexSection.watching,
      };
      const s = sectionMap[activeTab];
      if (s) navigate(`/tasks/${s}`);
    },
    [navigate]
  );

  const { data: todoTasks, isLoading: isTodoLoading } = useMyTodoTasks();
  const { overdueTasks, upcomingTasks } = useTaskDueSplit(todoTasks);

  const { data: todayResult, isLoading: isTodayLoading } = useEmployeeToday();
  const collectEsignRequests = useCallback(
    (items: ApiTodayItem[] | undefined) =>
      (items ?? [])
        .filter((item) => item.type === ApiTodayItemType.esign_request && item.esign_request)
        .map((item) => item.esign_request!),
    []
  );
  const overdueEsignRequests = useMemo(
    () => collectEsignRequests(todayResult?.overdue_items),
    [collectEsignRequests, todayResult?.overdue_items]
  );
  const upcomingEsignRequests = useMemo(
    () => collectEsignRequests(todayResult?.items),
    [collectEsignRequests, todayResult?.items]
  );

  const todoCount =
    (todoTasks?.length ?? 0) + overdueEsignRequests.length + upcomingEsignRequests.length;
  const isTodoTabLoading = isTodoLoading || isTodayLoading;

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiTaskFilter>({
    filters: [library.page_number],
    source: "Tasks",
  });

  const { data: archivedData, isLoading: isArchivedLoading } = useMyTasksSearch(
    { ...filter, is_archived: true },
    currentSection === TasksIndexSection.archived
  );

  const { data: completedData, isLoading: isCompletedLoading } = useMyTasksSearch(
    { ...filter, status: ApiTaskStatus.completed },
    currentSection === TasksIndexSection.completed
  );

  const { data: watchingData, isLoading: isWatchingLoading } = useEmployeeTasksSearch(
    employeeMe?.id ?? 0,
    ApiTaskEmployeeRole.watcher,
    { ...filter, is_archived: false },
    currentSection === TasksIndexSection.watching && !!employeeMe
  );

  return (
    <Page variant="opaque">
      <Page.Header title={t("tasks:my_tasks")} navigateTo={"/"} showBack={true} />
      <Page.Content>
        <Box className={"container-lg mx-auto"}>
          <TasksTodoCountText count={todoCount} variant={"primary"} />
          <Tabs
            onActiveTabChange={handleSectionChange}
            activeTab={currentSectionIndex}
            className={"mt-5"}
          >
            <TabItem title={t("tasks:section_title_todo")}>
              <Stack gap={"lg"} className={"mt-4"}>
                {(overdueTasks.length > 0 || overdueEsignRequests.length > 0) && (
                  <TasksSectionContent
                    title={t("tasks:section_title_overdue")}
                    titleWarning
                    tasks={overdueTasks}
                    esignRequests={overdueEsignRequests}
                    isLoading={isTodoTabLoading}
                  />
                )}
                <TasksSectionContent
                  title={t("tasks:section_title_todo")}
                  tasks={upcomingTasks}
                  esignRequests={upcomingEsignRequests}
                  isLoading={isTodoTabLoading}
                  emptyState={
                    overdueTasks.length === 0 &&
                    upcomingTasks.length === 0 &&
                    overdueEsignRequests.length === 0 &&
                    upcomingEsignRequests.length === 0 && (
                      <EmptyStateBase
                        emoji={emojiDefaults.task.default}
                        title={t("tasks:section_empty_title_todo")}
                        description={t("tasks:section_empty_description_todo")}
                      />
                    )
                  }
                />
              </Stack>
            </TabItem>
            <TabItem title={t("tasks:section_title_archived")}>
              <Stack gap={"lg"} className={"mt-4"}>
                <TasksSectionContent
                  title={t("tasks:section_title_archived")}
                  tasks={archivedData?.items ?? []}
                  isLoading={isArchivedLoading}
                  emptyState={
                    <EmptyStateBase
                      emoji={emojiDefaults.task.archived}
                      title={t("tasks:section_empty_title_archive")}
                      description={t("tasks:section_empty_description_archive")}
                    />
                  }
                />
                <PaginationBlock
                  metadata={archivedData?.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              </Stack>
            </TabItem>
            <TabItem title={t("tasks:section_title_completed")}>
              <Stack gap={"lg"} className={"mt-4"}>
                <TasksSectionContent
                  title={t("tasks:section_title_completed")}
                  tasks={completedData?.items ?? []}
                  isLoading={isCompletedLoading}
                  emptyState={
                    <EmptyStateBase
                      emoji={emojiDefaults.task.completed}
                      title={t("tasks:section_empty_title_done")}
                      description={t("tasks:section_empty_description_done")}
                    />
                  }
                />
                <PaginationBlock
                  metadata={completedData?.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              </Stack>
            </TabItem>
            <TabItem title={t("tasks:section_title_watching")}>
              <Stack gap={"lg"} className={"mt-4"}>
                <TasksSectionContent
                  title={t("tasks:section_title_watching")}
                  tasks={watchingData?.items ?? []}
                  isLoading={isWatchingLoading || !employeeMe}
                  emptyState={
                    <EmptyStateBase
                      emoji={emojiDefaults.task.default}
                      title={t("tasks:section_empty_title_watching")}
                    />
                  }
                />
                <PaginationBlock
                  metadata={watchingData?.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </Box>
      </Page.Content>
    </Page>
  );
}
